import { memo } from "react";

import { Badge, Typography } from "@bigbinary/neeto-atoms";
import classnames from "classnames";
import { hyphenate } from "neetocist";
import { joinHyphenCase } from "neetocommons/utils/general";
import PropTypes from "prop-types";
import { omit } from "ramda";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { UNSUPPORTED_LINK_PROPS } from "./constants";

const IntegrationItem = ({
  icon: Icon = null,
  description,
  label,
  path,
  href,
  isConnected,
  isMobileOrTablet = false,
  className = "",
  ...otherProps
}) => {
  const { t } = useTranslation();

  const commonProps = {
    "data-testid": `${hyphenate(label).replace(/\./g, "-")}-integration-card`,
    className: classnames(
      "relative flex cursor-pointer flex-col items-start gap-3 rounded-lg border border-border px-6 py-6 no-underline outline-none transition-shadow duration-300 ease-in-out md:py-8 lg:flex-row xl:gap-4",
      "hover:shadow-[0px_0px_9.2px_0px_rgba(29,33,59,0.12)] hover:[&_[data-settings-heading]]:text-primary",
      "active:shadow-[0px_0px_9.2px_0px_rgba(29,33,59,0.12)] active:[&_[data-settings-heading]]:text-primary",
      "focus:border-primary focus:shadow-[0px_0px_9.2px_0px_rgba(29,33,59,0.12)] focus:[&_[data-settings-heading]]:text-primary",
      "focus-visible:border-primary focus-visible:shadow-[0px_0px_9.2px_0px_rgba(29,33,59,0.12)] focus-visible:[&_[data-settings-heading]]:text-primary",
      className
    ),
    ...omit(UNSUPPORTED_LINK_PROPS, otherProps),
  };

  const LinkElement = href ? "a" : Link;
  const linkProps = href
    ? { href, ...commonProps }
    : { to: path, ...commonProps };

  return (
    <LinkElement {...linkProps}>
      {Icon && (
        <div className="border-border mt-0.5 flex shrink-0 items-center justify-center rounded-lg border p-1.5 shadow-sm xl:p-2.5">
          <Icon
            data-testid={`${joinHyphenCase(label)}-integration-icon`}
            size={isMobileOrTablet ? 26 : 32}
          />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Typography asChild variant="h3" weight="semibold">
          <span
            className="text-foreground text-lg leading-6 transition-colors duration-300 ease-in-out"
            data-settings-heading=""
            data-testid="settings-item-heading"
          >
            {label}
          </span>
        </Typography>
        <Typography asChild variant="body2">
          <p
            className="text-muted-foreground"
            data-testid="settings-item-description"
          >
            {description}
          </p>
        </Typography>
      </div>
      {isConnected && (
        <Badge
          className="absolute top-2 right-2"
          data-testid="integration-status-tag"
          variant="default"
        >
          {t("neetoMolecules.integrationCard.connected")}
        </Badge>
      )}
    </LinkElement>
  );
};

IntegrationItem.propTypes = {
  /**
   * The icon component to display. Accepts lucide-react icons or brand logos.
   */
  icon: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func,
    PropTypes.object,
  ]),
  /**
   * A description of the integration.
   */
  description: PropTypes.string,
  /**
   * The label text for the integration item.
   */
  label: PropTypes.string.isRequired,
  /**
   * The internal route path for the integration item link.
   */
  path: PropTypes.string,
  /**
   * The external URL for the integration item link.
   */
  href: PropTypes.string,
  /**
   * Whether the integration is currently connected.
   */
  isConnected: PropTypes.bool,
  /**
   * Whether the current viewport is mobile or tablet size.
   */
  isMobileOrTablet: PropTypes.bool,
  /**
   * Additional CSS classes for the link element.
   */
  className: PropTypes.string,
};

export default memo(IntegrationItem);
